import { Hash } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useAppStore, type NumericGroupingMode } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { NUMERIC_GROUPING_OPTIONS } from '../../../workspace/common/components/numericGroupingOptions';

/**
 * Settings → UI → Behavior row: "Thousands separators in results".
 *
 * PP-01 — picks the `NumericGroupingMode` BOTH results grids use to render
 * quantity columns. Display only: copy, export and edit always use the raw
 * value, and keys / ID-like columns are never grouped regardless of the mode
 * (`shouldGroupNumericColumn`). The same three choices sit in the grids' cell
 * context menu (`NumericGroupingMenu`) as a quick toggle.
 */
export function NumericGroupingSection() {
  const numericGrouping = useAppStore((state) => state.numericGrouping);
  const setNumericGrouping = useAppStore((state) => state.setNumericGrouping);
  const { track } = useAnalytics();

  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
      <div className="flex items-center gap-3">
        <Hash className="h-5 w-5 text-muted-foreground" />
        <div className="grid gap-0.5">
          <Label htmlFor="numeric-grouping" className="cursor-pointer">
            Thousands separators in results
          </Label>
          <div className="text-xs text-muted-foreground">
            Display only. Copy, export and edit always use the exact stored value. Keys and ID-like columns are never grouped.
          </div>
        </div>
      </div>
      <Select
        value={numericGrouping}
        onValueChange={(value) => {
          const next = value as NumericGroupingMode;
          if (next === numericGrouping) return;
          track('feature:numeric_grouping_changed', { from: numericGrouping, to: next, surface: 'settings' });
          setNumericGrouping(next);
        }}
      >
        <SelectTrigger
          id="numeric-grouping"
          className="w-[200px]"
          data-testid="numeric-grouping-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {NUMERIC_GROUPING_OPTIONS.map((option) => (
            <SelectItem
              key={option.mode}
              value={option.mode}
              data-testid={`numeric-grouping-${option.mode}`}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
